import { IService } from '@riseart/interfaces';
import * as JwtLib from 'jsonwebtoken';
import { EncryptedToken } from './tokens/EncryptedToken.js';
import { DecryptedToken } from './tokens/DecryptedToken.js';
import { DecodedToken } from './tokens/DecodedToken.js';
import { InvalidToken } from './errors/InvalidToken.js';
import { ExpiredToken } from './errors/ExpiredToken.js';

const LEEWAY_DEFAULT = 86400;
const ALGORITHM_DEFAULT = 'HS512';

/**
 * Jwt
 */
class Jwt implements IService {
  /**
   * getSignatureKey
   *
   * @param {string} signatureKey
   * @returns {string}
   */
  private static getSignatureKey(signatureKey?: string): string {
    return signatureKey || process.env.JWT_SIGNATURE_KEY;
  }

  /**
   * Sign
   *
   * @param {Record<string, unknown>} payload
   * @param {number} expireTime
   * @param {string} signatureKey
   * @param {JwtLib.Algorithm} algorithm
   * @returns {Promise<EncryptedToken>}
   */
  public static async sign(
    payload: Record<string, unknown>,
    expireTime: number,
    signatureKey?: string,
    algorithm: JwtLib.Algorithm = ALGORITHM_DEFAULT,
  ): Promise<EncryptedToken> {
    return new Promise<EncryptedToken>((resolve, reject) =>
      JwtLib.sign(
        { ...payload, exp: Math.floor(Date.now() / 1000) + expireTime },
        Jwt.getSignatureKey(signatureKey),
        { algorithm },
        (error, token) => (error ? reject(new InvalidToken(error)) : resolve(new EncryptedToken(token))),
      ),
    );
  }

  /**
   * Verify
   *
   * @param {EncryptedToken} token
   * @param {string} signatureKey
   * @param {string} algorithm
   * @returns {Promise<DecryptedToken>}
   */
  public static async verify(
    token: EncryptedToken,
    signatureKey?: string,
    options: JwtLib.VerifyOptions = {},
  ): Promise<DecryptedToken> {
    const combinedOptions = {
      // default options
      algorithm: ALGORITHM_DEFAULT,
      clockTolerance: LEEWAY_DEFAULT,

      // custom options
      ...(options || {}),

      // fixed options
      ignoreExpiration: false,
      ignoreNotBefore: false,
    };
    return new Promise<DecryptedToken>((resolve, reject) =>
      JwtLib.verify(
        token.toString(),
        Jwt.getSignatureKey(signatureKey),
        combinedOptions,
        (error, decoded: Record<string, any>) =>
          error ? reject(new InvalidToken(error)) : resolve(new DecryptedToken(decoded)),
      ),
    );
  }

  /**
   * Decode
   *
   * @returns {Promise<DecodedToken>}
   */
  public static async decode(
    token: EncryptedToken,
    leeway: number = LEEWAY_DEFAULT,
    forceJson = true,
  ): Promise<DecodedToken> {
    try {
      return new Promise<DecodedToken>((resolve, reject) => {
        const decodedData = JwtLib.decode(token.toString(), {
          json: forceJson,
          complete: true,
        });
        const decodedToken = new DecodedToken(decodedData);
        if (decodedToken.payload) {
          if (
            typeof decodedToken.payload.exp === 'undefined' ||
            decodedToken.payload.exp - leeway >= Math.floor(Date.now() / 1000)
          ) {
            resolve(decodedToken);
          } else {
            reject(new ExpiredToken(decodedToken));
          }
        }
        reject(new InvalidToken());
      });
    } catch (Error) {
      throw new InvalidToken(Error);
    }
  }

  /**
   * GetStatus
   *
   * @returns {Array<unknown>}
   */
  public getStatus(): Array<unknown> {
    throw new Error('Jwt.getStatus() method is not implemented yet');
  }
}

export { Jwt };
